/**
 * EMR Action Dialog Component
 *
 * Renders a Material-UI dialog for collecting user input before executing an action.
 * Displays form fields based on the dialog definition from the action.
 *
 * Single Responsibility: Dialog UI rendering
 * All logic is handled by the parent hook (useEmrActionHandler)
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from '@mui/material'

export default function EmrActionDialog({
  open = false,
  onClose,
  dialogDef,
  onSubmit,
}) {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({})
      setErrors({})
    }
  }, [open])

  if (!dialogDef) return null

  const { title, submitLabel = 'Submit', danger = false, fields = [] } = dialogDef

  /**
   * Handle field value change
   */
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }))

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[fieldName]
        return newErrors
      })
    }
  }

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    const newErrors = {}

    fields.forEach((field) => {
      const value = formData[field.name]

      // Check required fields
      if (field.required && (!value || value.trim() === '')) {
        newErrors[field.name] = `${field.label} is required`
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData)
    }
  }

  /**
   * Render a single form field
   */
  const renderField = (field) => {
    const { name, label, type = 'text', required = false, placeholder = '' } = field
    const value = formData[name] || ''
    const error = errors[name]

    switch (type) {
      case 'textarea':
        return (
          <TextField
            key={name}
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
            required={required}
            error={!!error}
            helperText={error}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        )

      case 'checkbox':
        return (
          <FormControlLabel
            key={name}
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => handleFieldChange(name, e.target.checked)}
              />
            }
            label={label}
            sx={{ mb: 2 }}
          />
        )

      case 'text':
      case 'number':
      case 'email':
      default:
        return (
          <TextField
            key={name}
            fullWidth
            type={type}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFieldChange(name, e.target.value)}
            required={required}
            error={!!error}
            helperText={error}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="emr-action-dialog-title"
    >
      <DialogTitle id="emr-action-dialog-title">
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {fields.map((field) => renderField(field))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={danger ? 'error' : 'primary'}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
